// src/hooks/useTableCustomization.ts
// Table customization state for any data-table page.
// Persists per-table via localStorage so the user's view survives reloads.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildDefaultCustomization } from '../components/common/tableCustomization';
import type { TableColumnDef, TableCustomization } from '../components/common/tableCustomization';

interface UseTableCustomizationReturn {
  customization: TableCustomization;
  draft: TableCustomization;
  isCustomized: boolean;
  updateDraft: (patch: Partial<TableCustomization>) => void;
  applyChanges: () => void;
  resetToDefault: () => void;
  cancelDraft: () => void;
}

const STORAGE_KEY_PREFIX = 'novala:table-customization:';

function loadCustomization(tableKey: string, defs: TableColumnDef[]): TableCustomization {
  const fallback = buildDefaultCustomization(defs);
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + tableKey);
    if (raw) {
      const parsed = JSON.parse(raw) as TableCustomization;
      // Make sure new columns added since last save are present
      const knownKeys = new Set(parsed.columns.map(function (c) { return c.key; }));
      defs.forEach(function (d, i) {
        if (!knownKeys.has(d.key)) {
          parsed.columns.push({ key: d.key, visible: d.defaultVisible !== false, order: i + 100 });
        }
      });
      return parsed;
    }
  } catch (e) {
    // ignore
  }
  return fallback;
}

function saveCustomization(tableKey: string, c: TableCustomization): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + tableKey, JSON.stringify(c));
  } catch (e) {
    // ignore
  }
}

export function useTableCustomization(tableKey: string, defs: TableColumnDef[]): UseTableCustomizationReturn {
  const defaults = useMemo(function () { return buildDefaultCustomization(defs); }, [defs]);
  const [customization, setCustomization] = useState<TableCustomization>(function () { return loadCustomization(tableKey, defs); });
  const [draft, setDraft] = useState<TableCustomization>(customization);

  useEffect(function () { setDraft(customization); }, [customization]);

  const isCustomized = useMemo(function () {
    return JSON.stringify(customization) !== JSON.stringify(defaults);
  }, [customization, defaults]);

  const updateDraft = useCallback(function (patch: Partial<TableCustomization>) {
    setDraft(function (d) { return { ...d, ...patch }; });
  }, []);

  const applyChanges = useCallback(function () {
    setCustomization(draft);
    saveCustomization(tableKey, draft);
  }, [draft, tableKey]);

  const resetToDefault = useCallback(function () {
    setCustomization(defaults);
    setDraft(defaults);
    saveCustomization(tableKey, defaults);
  }, [defaults, tableKey]);

  const cancelDraft = useCallback(function () {
    setDraft(customization);
  }, [customization]);

  return { customization, draft, isCustomized, updateDraft, applyChanges, resetToDefault, cancelDraft };
}